import React, { useContext, useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";

const path = require("path");

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark";
  const primary = theme.palette.primary.main;

  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
    },
    dialogPaper: {
      borderRadius: 10,
      color: isDark ? "#f8fafc" : "#131b2e",
      backgroundColor: isDark ? "#424242" : "#ffffff",
      boxShadow: isDark
        ? "0 24px 56px rgba(0, 0, 0, 0.42)"
        : "0 24px 56px rgba(15, 23, 42, 0.16)",
    },
    dialogTitle: {
      padding: theme.spacing(2.5, 3, 1.5),
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
    },
    titleIcon: {
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#ffffff",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 10px 22px rgba(0, 0, 0, 0.28)"
        : "0 10px 22px rgba(0, 82, 204, 0.2)",
    },
    dialogEyebrow: {
      color: isDark ? "#93c5fd" : "#4c5d8d",
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: 0.5,
      textTransform: "uppercase",
    },
    dialogTitleText: {
      marginTop: 2,
      color: isDark ? "#ffffff" : "#131b2e",
      fontSize: 22,
      fontWeight: 800,
      lineHeight: 1.2,
    },
    dialogContent: {
      padding: theme.spacing(1, 3, 2),
      backgroundColor: isDark ? "#424242" : "#ffffff",
    },
    field: {
      "& .MuiInputLabel-root": {
        color: isDark ? "#d1d5db" : "#4b5563",
        fontWeight: 600,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: primary,
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        color: isDark ? "#ffffff" : "#131b2e",
        backgroundColor: isDark ? "#4e4e4e" : "#f8fbff",
        "& fieldset": {
          borderColor: isDark
            ? "rgba(255, 255, 255, 0.24)"
            : "rgba(195, 198, 214, 0.65)",
        },
        "&:hover fieldset": {
          borderColor: isDark
            ? "rgba(147, 197, 253, 0.72)"
            : "rgba(0, 82, 204, 0.55)",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 1,
        },
      },
      "& .MuiFormHelperText-root": {
        color: isDark ? "#fca5a5" : undefined,
      },
    },
    formControl: {
      "& .MuiInputLabel-root": {
        color: isDark ? "#d1d5db" : "#4b5563",
        fontWeight: 600,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: primary,
      },
      "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        color: isDark ? "#ffffff" : "#131b2e",
        backgroundColor: isDark ? "#4e4e4e" : "#f8fbff",
        "& fieldset": {
          borderColor: isDark
            ? "rgba(255, 255, 255, 0.24)"
            : "rgba(195, 198, 214, 0.65)",
        },
        "&:hover fieldset": {
          borderColor: isDark
            ? "rgba(147, 197, 253, 0.72)"
            : "rgba(0, 82, 204, 0.55)",
        },
        "&.Mui-focused fieldset": {
          borderColor: primary,
          borderWidth: 1,
        },
      },
      "& .MuiSelect-icon": {
        color: isDark ? "#d1d5db" : "inherit",
      },
    },
    variablesWrap: {
      padding: theme.spacing(1),
      borderRadius: 8,
      backgroundColor: isDark ? "#4e4e4e" : "#f2f6ff",
    },
    attachmentCard: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      borderRadius: 8,
      backgroundColor: isDark ? "#4e4e4e" : "#f2f6ff",
    },
    attachmentButton: {
      justifyContent: "flex-start",
      minWidth: 0,
      color: isDark ? "#dbeafe" : "#0040a2",
      textTransform: "none",
      fontWeight: 700,
      overflow: "hidden",
    },
    deleteMediaButton: {
      color: isDark ? "#fecaca" : "#ba1a1a",
      backgroundColor: isDark ? "rgba(239, 68, 68, 0.16)" : "#ffdad6",
      "&:hover": {
        backgroundColor: isDark ? "rgba(239, 68, 68, 0.24)" : "#ffc7c0",
      },
    },
    dialogActions: {
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 3, 2.5),
      backgroundColor: isDark ? "#424242" : "#ffffff",
      [theme.breakpoints.down("xs")]: {
        alignItems: "stretch",
        flexDirection: "column",
        "& > *": {
          marginLeft: "0 !important",
        },
      },
    },
    attachAction: {
      marginRight: "auto",
      borderRadius: 8,
      color: isDark ? "#dbeafe" : "#0040a2",
      borderColor: isDark
        ? "rgba(147, 197, 253, 0.42)"
        : "rgba(0, 82, 204, 0.42)",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": {
        borderColor: primary,
        backgroundColor: isDark ? "rgba(0, 82, 204, 0.18)" : "#eef4ff",
      },
      [theme.breakpoints.down("xs")]: {
        marginRight: 0,
      },
    },
    cancelButton: {
      borderRadius: 8,
      color: isDark ? "#e5e7eb" : "#4b5563",
      borderColor: isDark ? "rgba(255, 255, 255, 0.24)" : "rgba(75, 85, 99, 0.28)",
      textTransform: "none",
      fontWeight: 700,
      "&:hover": {
        backgroundColor: isDark ? "#4e4e4e" : "#f3f4f6",
      },
    },
    saveButton: {
      position: "relative",
      borderRadius: 8,
      minWidth: 120,
      color: "#ffffff",
      background: `linear-gradient(135deg, ${primary} 0%, #0052cc 100%)`,
      boxShadow: isDark
        ? "0 10px 22px rgba(0, 0, 0, 0.28)"
        : "0 10px 22px rgba(0, 82, 204, 0.22)",
      textTransform: "none",
      fontWeight: 800,
      "&:hover": {
        background: "linear-gradient(135deg, #0040a2 0%, #0052cc 100%)",
      },
    },
    buttonProgress: {
      color: "#ffffff",
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  };
});

const QuickeMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required("Obrigatorio"),
});

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const messageInputRef = useRef();

  const initialState = {
    shortcode: "",
    message: "",
    geral: false,
    status: true,
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;

        const { data } = await api.get(`/quick-messages/${quickemessageId}`);

        setQuickemessage((prevState) => {
          return { ...prevState, ...data };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [quickemessageId, open]);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveQuickeMessage = async (values) => {
    const quickemessageData = {
      ...values,
      isMedia: true,
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
          ? path.basename(values.mediaPath).replace(/ /g, "_")
          : null,
    };

    try {
      if (quickemessageId) {
        await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(
            `/quick-messages/${quickemessageId}/media-upload`,
            formData
          );
        }
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload === "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) {
        attachmentFile.current.value = null;
      }
    }

    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload === "function") {
        reload();
      }
    }
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const isReadOnly = (values) => quickemessageId && values.visao && !values.geral && values.userId !== user.id;

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Box className={classes.titleRow}>
            <Box className={classes.titleIcon}>
              <FlashOnIcon />
            </Box>
            <Box>
              <Box className={classes.dialogEyebrow}>Resposta rapida</Box>
              <Box className={classes.dialogTitleText}>
                {quickemessageId
                  ? `${i18n.t("quickMessages.dialog.edit")}`
                  : `${i18n.t("quickMessages.dialog.add")}`}
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={quickemessage}
          enableReinitialize
          validationSchema={QuickeMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickeMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form>
              <DialogContent className={classes.dialogContent}>
                <Grid spacing={2} container>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      autoFocus
                      className={classes.field}
                      label={i18n.t("quickMessages.dialog.shortcode")}
                      name="shortcode"
                      disabled={isReadOnly(values)}
                      error={touched.shortcode && Boolean(errors.shortcode)}
                      helperText={touched.shortcode && errors.shortcode}
                      variant="outlined"
                      margin="dense"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <Field
                      as={TextField}
                      className={classes.field}
                      label={i18n.t("quickMessages.dialog.message")}
                      name="message"
                      inputRef={messageInputRef}
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      variant="outlined"
                      margin="dense"
                      disabled={isReadOnly(values)}
                      multiline
                      rows={7}
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <Box className={classes.variablesWrap}>
                      <MessageVariablesPicker
                        disabled={isSubmitting || isReadOnly(values)}
                        onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                      />
                    </Box>
                  </Grid>
                  <Grid xs={12} sm={6} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="visao-selection-label">
                        {i18n.t("quickMessages.dialog.visao")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("quickMessages.dialog.visao")}
                        labelId="visao-selection-label"
                        id="visao"
                        disabled={isReadOnly(values)}
                        name="visao"
                        onChange={(e) => {
                          setFieldValue("visao", e.target.value === "true");
                        }}
                        error={touched.visao && Boolean(errors.visao)}
                        value={values.visao ? "true" : "false"}
                      >
                        <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                        <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  {values.visao === true && (
                    <Grid xs={12} sm={6} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="geral-selection-label">
                          {i18n.t("quickMessages.dialog.geral")}
                        </InputLabel>
                        <Field
                          as={Select}
                          label={i18n.t("quickMessages.dialog.geral")}
                          labelId="geral-selection-label"
                          id="geral"
                          name="geral"
                          disabled={isReadOnly(values)}
                          value={values.geral ? "true" : "false"}
                          error={touched.geral && Boolean(errors.geral)}
                        >
                          <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                          <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                  )}
                  {(quickemessage.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Box className={classes.attachmentCard}>
                        <Button
                          type="button"
                          className={classes.attachmentButton}
                          startIcon={<AttachFileIcon />}
                        >
                          {attachment ? attachment.name : quickemessage.mediaName}
                        </Button>
                        <IconButton
                          type="button"
                          className={classes.deleteMediaButton}
                          onClick={() => setConfirmationOpen(true)}
                          disabled={isReadOnly(values)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                {!attachment && !quickemessage.mediaPath && (
                  <Button
                    type="button"
                    className={classes.attachAction}
                    color="primary"
                    onClick={() => attachmentFile.current.click()}
                    disabled={isSubmitting || isReadOnly(values)}
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                  >
                    {i18n.t("quickMessages.buttons.attach")}
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  variant="outlined"
                  className={classes.cancelButton}
                >
                  {i18n.t("quickMessages.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || isReadOnly(values)}
                  variant="contained"
                  className={classes.saveButton}
                >
                  {quickemessageId
                    ? `${i18n.t("quickMessages.buttons.edit")}`
                    : `${i18n.t("quickMessages.buttons.add")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickMessageDialog;
